import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  CircularProgress
} from '@mui/material';

import ConfigurationForm from './ConfigurationForm';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/apiUtils';
import { createLogger } from '../../utils/debugUtils';

const logger = createLogger('ResearchAgentNewProject');

/**
 * ResearchAgentNewProject Component
 * Dedicated page for creating new research jobs
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Callback when job is created successfully
 */
const ResearchAgentNewProject = React.forwardRef((props, ref) => {
  const { onSuccess } = props;
  
  useAuth(); // Keep auth context for future use
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availableFilters, setAvailableFilters] = useState([]);

  // Fetch available filters on component mount
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch filters for the source dropdown
  const fetchFilters = async () => {
    setLoading(true);
    try {
      const filters = await apiGet('/api/filters?includeCounts=true');
      setAvailableFilters(filters);
      logger.info(`Loaded ${filters.length} filters for source selection`);
    } catch (error) {
      logger.error('Error loading filters:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle job creation
  const handleCreateJob = async (jobConfig) => {
    setSubmitting(true);
    try {
      // Call API to create job with proper authentication
      const data = await apiPost('/api/research/jobs', jobConfig);
      
      if (data) {
        logger.info('Research job created successfully:', data);
      }
      
      if (data && data.jobId) {
        logger.info('Research job created with ID:', data.jobId);
      } else if (data && data.job && data.job.jobId) {
        logger.info('Research job created with ID:', data.job.jobId);
      } else {
        logger.info('Research job created successfully');
      }
      
    } catch (error) {
      logger.error('Error creating research job:', error);
      // Error handling would be done in the configuration form
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper 
          variant="outlined" 
          sx={{ 
            p: 2, 
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ 
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px'
            }
          }}>
            <ConfigurationForm 
              availableFilters={availableFilters} 
              onCreateJob={handleCreateJob} 
              isSubmitting={submitting}
              onSuccess={onSuccess}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
});

export default ResearchAgentNewProject;
